// Secure cookie helpers for MSR. Enforces Secure, SameSite, and consent gating.

export interface CookieConsent {
  v: number;
  shown: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

interface CookieOptions {
  name: string;
  value: string;
  category: CookieCategory;
  maxAgeSeconds: number;
  sameSite?: 'Strict' | 'Lax';
}

const CONSENT_COOKIE_NAME = 'msr_cookie_consent';

export function readConsentCookie(): CookieConsent | null {
  const raw = getCookie(CONSENT_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.v !== 'number') return null;
    return parsed as CookieConsent;
  } catch {
    return null;
  }
}

export function writeConsentCookie(consent: CookieConsent): void {
  setCookie({
    name: CONSENT_COOKIE_NAME,
    value: JSON.stringify(consent),
    category: 'necessary',
    maxAgeSeconds: 31536000, // 1 year per ICO guidance
    sameSite: 'Lax',
  });
}

export function setCookie(opts: CookieOptions): void {
  if (opts.category !== 'necessary') {
    const consent = readConsentCookie();
    if (!consent?.[opts.category]) {
      console.warn(`[cookies] Refusing to set ${opts.category} cookie without consent`);
      return;
    }
  }
  const parts = [
    `${opts.name}=${encodeURIComponent(opts.value)}`,
    `Max-Age=${opts.maxAgeSeconds}`,
    `Path=/`,
    `Secure`,
    `SameSite=${opts.sameSite ?? 'Lax'}`,
  ];
  document.cookie = parts.join('; ');
}

export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

export function deleteCookie(name: string): void {
  document.cookie = `${name}=; Max-Age=0; Path=/; Secure; SameSite=Lax`;
}
